// V9.22 · Configuração de recebimentos e taxas por conta financeira
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Percent, Plus, Trash2 } from 'lucide-react';
import api from '@/lib/api';

const METHODS = [
  ['pix', 'PIX'], ['cash', 'Dinheiro'], ['card_debit', 'Débito'],
  ['card_credit', 'Crédito'], ['pix_card', 'PIX no cartão'],
  ['boleto', 'Boleto'], ['transfer', 'Transferência'],
];

export const ConfigurarTaxasConta = ({ conta }) => {
  const [open, setOpen] = useState(false);
  const [accepts, setAccepts] = useState({});
  const [rules, setRules] = useState([]);
  const [provider, setProvider] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/financial/accounts/receiving');
        const a = (data.accounts || []).find((x) => x.id === conta.id) || {};
        setAccepts(a.accepts || {});
        setRules(a.fee_matrix || []);
        setProvider(a.provider || '');
      } catch (_) { toast.error('Não foi possível carregar a configuração da conta.'); }
    })();
  }, [open, conta.id]);

  const setRule = (i, k, v) => setRules((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const salvar = async () => {
    setBusy(true);
    try {
      await api.put(`/financial/accounts/${conta.id}/config`, {
        accepts, provider,
        fee_matrix: rules.map((r) => ({
          payment_method: r.payment_method,
          installments_from: Number(r.installments_from || 1),
          installments_to: Number(r.installments_to || 1),
          base_fee_percent: Number(r.base_fee_percent || 0),
          monthly_fee_percent: Number(r.monthly_fee_percent || 0),
          fixed_fee: Number(r.fixed_fee || 0),
          settlement_days: Number(r.settlement_days || 0),
          active: r.active !== false,
        })),
      });
      toast.success('Taxas e recebimentos salvos.');
      setOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar.'); }
    finally { setBusy(false); }
  };

  const seedPagSeguro = async () => {
    try {
      const { data } = await api.post(`/financial/accounts/${conta.id}/seed-pagseguro-fees`);
      setRules(data.fee_matrix);
      setProvider('PagSeguro');
      toast.success('Tabela PagSeguro carregada — ajuste se precisar e salve.');
    } catch (err) { toast.error('Erro ao carregar a tabela.'); }
  };

  return (
    <>
      <Button size="sm" variant="ghost" title="Configurar taxas e recebimentos"
        onClick={() => setOpen(true)} data-testid={`btn-config-taxas-${conta.id}`}>
        <Percent className="w-4 h-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="dialog-config-taxas">
          <DialogHeader>
            <DialogTitle>Taxas e recebimentos · {conta.nome}</DialogTitle>
            <DialogDescription>
              Formas aceitas e taxa por método/parcelamento. O sistema usa exatamente o que estiver salvo aqui.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase text-muted-foreground">Adquirente / gateway</label>
              <Input value={provider} onChange={(e) => setProvider(e.target.value)}
                placeholder="PagSeguro, Mercado Pago, Safra…" data-testid="config-provider" />
            </div>

            <div>
              <p className="text-xs font-bold uppercase text-muted-foreground mb-1">Formas aceitas</p>
              <div className="flex flex-wrap gap-3">
                {METHODS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1.5 text-sm">
                    <Checkbox checked={!!accepts[k]}
                      onCheckedChange={(v) => setAccepts((a) => ({ ...a, [k]: !!v }))}
                      data-testid={`config-accept-${k}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase text-muted-foreground">Matriz de taxas</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={seedPagSeguro} data-testid="config-seed-pagseguro">
                  Carregar tabela PagSeguro
                </Button>
                <Button size="sm" variant="outline" data-testid="config-add-rule"
                  onClick={() => setRules((rs) => [...rs, { payment_method: 'card_credit', installments_from: 1, installments_to: 1, base_fee_percent: 0, monthly_fee_percent: 0, fixed_fee: 0, settlement_days: 1, active: true }])}>
                  <Plus className="w-3 h-3 mr-1" /> Regra
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto rounded border">
              <table className="w-full text-xs">
                <thead className="bg-muted"><tr>
                  <th className="p-2 text-left">Método</th><th className="p-2">De</th><th className="p-2">Até</th>
                  <th className="p-2">Taxa base %</th><th className="p-2">Acréscimo mensal %</th>
                  <th className="p-2">Fixo R$</th><th className="p-2">D+</th><th className="p-2" />
                </tr></thead>
                <tbody>
                  {rules.length === 0 && (
                    <tr><td colSpan={8} className="p-3 text-center text-muted-foreground">
                      Nenhuma regra — sem regra a taxa fica zero.</td></tr>
                  )}
                  {rules.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-1">
                        <select className="h-8 border rounded px-1 w-32" value={r.payment_method}
                          onChange={(e) => setRule(i, 'payment_method', e.target.value)}>
                          {METHODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                      </td>
                      {['installments_from', 'installments_to', 'base_fee_percent', 'monthly_fee_percent', 'fixed_fee', 'settlement_days'].map((k) => (
                        <td key={k} className="p-1">
                          <Input type="number" step="0.01" className="h-8 w-20" value={r[k] ?? ''}
                            onChange={(e) => setRule(i, k, e.target.value)} data-testid={`config-rule-${i}-${k}`} />
                        </td>
                      ))}
                      <td className="p-1 text-center">
                        <Button size="sm" variant="ghost" className="text-red-600"
                          onClick={() => setRules((rs) => rs.filter((_, idx) => idx !== i))}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={salvar} disabled={busy} data-testid="config-salvar">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar configuração
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConfigurarTaxasConta;
